import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getDcaiPlans,
  getDcaiPnl,
  postDcaiPlans,
  postDcaiPlansIdDisable,
  getDcaiPlansIdCycles,
  postDcaiTemporalPlansIdProcessCycle,
} from "../api/endpoints/dcai/dcai";
import { CreateDcaiPlanDTOFrequency } from "../api/models/createDcaiPlanDTOFrequency";
import { ProcessDcaiCycleBodyDTOForceCategory } from "../api/models/processDcaiCycleBodyDTOForceCategory";
import type { DcaiPnlAggregateResponse } from "../api/models/dcaiPnlAggregateResponse";
import type { CreateDcaiPlanDTO } from "../api/models/createDcaiPlanDTO";
import type { ProcessDcaiCycleBodyDTO } from "../api/models/processDcaiCycleBodyDTO";
import type { DcaiPlanResponse } from "../api/models/dcaiPlanResponse";
import type { DcaiCycleResponse } from "../api/models/dcaiCycleResponse";

import "./DcaiPage.css";

// Rótulos pt-BR para os enums vindos da API.
const FREQUENCY_LABELS: Record<string, string> = {
  HALF_HOURLY: "A cada 30 minutos",
  MINUTELY: "A cada minuto (teste)",
  WEEKLY: "Semanal",
  BIWEEKLY: "Quinzenal",
  MONTHLY: "Mensal",
  QUARTERLY: "Trimestral",
};

const PLAN_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Rascunho",
  ACTIVE: "Ativo",
  DISABLING: "Desativando",
  DISABLED: "Desativado",
};

const CYCLE_STATUS_LABELS: Record<string, string> = {
  COMPLETED: "Concluído",
  SKIPPED: "Pulado",
  FAILED: "Falhou",
};

const SIGNAL_LABELS: Record<string, string> = {
  PESSIMO: "Péssimo",
  RUIM: "Ruim",
  OK: "Ok",
  BOM: "Bom",
  OTIMO: "Ótimo",
};

// Estado inicial do formulário de criação.
const EMPTY_FORM: CreateDcaiPlanDTO = {
  baseCoin: "BTC",
  quoteCoin: "USDT",
  contributionAmount: "",
  frequency: CreateDcaiPlanDTOFrequency.WEEKLY,
  name: "",
};

// Extrai a mensagem de erro do envelope padrão da API.
const errorMessage = (data: unknown, fallback: string): string => {
  if (data && typeof data === "object" && "message" in data) {
    const msg = (data as { message?: string }).message;
    if (msg) return msg;
  }
  return fallback;
};

const formatDateTime = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR");
};

const formatSignedDecimalValue = (value: string | null | undefined): string => {
  if (value == null) return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  const formatted = parsed.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return parsed > 0 ? `+${formatted}` : formatted;
};

const pnlValueClass = (value: string | null | undefined): string => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  if (parsed > 0) return "dcai-pnlValue--positive";
  if (parsed < 0) return "dcai-pnlValue--negative";
  return "dcai-pnlValue--neutral";
};

const formatCurrencyLabel = (
  value: string | null | undefined,
  coin: string | null | undefined,
): string => {
  if (value == null) return "—";
  return `${formatSignedDecimalValue(value)} ${coin ?? ""}`.trim();
};

const formatPnlCombined = (
  unrealizedPnl: string | null | undefined,
  pnlPercent: string | null | undefined,
  coin: string | null | undefined,
): string => {
  if (unrealizedPnl == null || pnlPercent == null) return "—";
  const percent = formatSignedDecimalValue(pnlPercent);
  return `${formatSignedDecimalValue(unrealizedPnl)} ${coin ?? ""} (${percent}%)`.trim();
};

// O tipo gerado é `objeto | null`; trabalhamos só com a variante não-nula.
type DcaiCycle = NonNullable<DcaiCycleResponse>;

interface CyclesState {
  loading: boolean;
  error: string | null;
  items: DcaiCycle[];
  loaded: boolean;
}

export default function DcaiPage() {
  const navigate = useNavigate();

  const [plans, setPlans] = useState<DcaiPlanResponse[]>([]);
  const [pnlSummary, setPnlSummary] = useState<DcaiPnlAggregateResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pnlError, setPnlError] = useState<string | null>(null);

  // Formulário de criação.
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateDcaiPlanDTO>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Estado por plano: desativação, expansão e ciclos.
  const [disablingId, setDisablingId] = useState<string | null>(null);
  const [disableError, setDisableError] = useState<Record<string, string>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Record<string, CyclesState>>({});

  // Disparo manual de ciclo (QA): categoria forçada, processamento e feedback.
  const [forceCategory, setForceCategory] = useState<Record<string, string>>(
    {},
  );
  const [forcePrice, setForcePrice] = useState<Record<string, string>>({});
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [processError, setProcessError] = useState<Record<string, string>>({});
  const [processResult, setProcessResult] = useState<Record<string, string>>(
    {},
  );

  const fetchPlans = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) setLoading(true);
    setError(null);
    setPnlError(null);
    try {
      const [plansResult, pnlResult] = await Promise.allSettled([
        getDcaiPlans(),
        getDcaiPnl(),
      ]);

      if (plansResult.status === "fulfilled") {
        const response = plansResult.value;
        if (response.status === 200) {
          setPlans(response.data.data);
        } else {
          setError(
            errorMessage(
              response.data,
              `Falha ao buscar planos (status ${response.status}).`,
            ),
          );
        }
      } else {
        console.error("Erro ao buscar planos DCAI:", plansResult.reason);
        setError(
          plansResult.reason instanceof Error
            ? plansResult.reason.message
            : "Erro desconhecido",
        );
      }

      if (pnlResult.status === "fulfilled") {
        const response = pnlResult.value;
        if (response.status === 200) {
          setPnlSummary(response.data.data);
        } else {
          setPnlSummary(null);
          setPnlError(
            errorMessage(
              response.data,
              `Falha ao buscar PnL do DCAI (status ${response.status}).`,
            ),
          );
        }
      } else {
        console.error("Erro ao buscar PnL DCAI:", pnlResult.reason);
        setPnlSummary(null);
        setPnlError(
          pnlResult.reason instanceof Error
            ? pnlResult.reason.message
            : "Erro desconhecido",
        );
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Ref para ler o plano expandido dentro do polling sem recriar o intervalo.
  const expandedIdRef = useRef<string | null>(null);
  useEffect(() => {
    expandedIdRef.current = expandedId;
  }, [expandedId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validação básica de campos obrigatórios.
    const required: (keyof CreateDcaiPlanDTO)[] = [
      "baseCoin",
      "quoteCoin",
      "contributionAmount",
    ];
    for (const field of required) {
      if (!String(form[field]).trim()) {
        setFormError("Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await postDcaiPlans({
        baseCoin: form.baseCoin.trim().toUpperCase(),
        quoteCoin: form.quoteCoin.trim().toUpperCase(),
        contributionAmount: form.contributionAmount.trim(),
        frequency: form.frequency,
        name: form.name?.trim() || undefined,
      });
      if (response.status === 201) {
        setForm(EMPTY_FORM);
        setShowForm(false);
        await fetchPlans({ silent: true });
      } else {
        setFormError(
          errorMessage(
            response.data,
            `Falha ao criar plano (status ${response.status}).`,
          ),
        );
      }
    } catch (err) {
      console.error("Erro ao criar plano DCAI:", err);
      setFormError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (id: string) => {
    setDisablingId(id);
    setDisableError((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const response = await postDcaiPlansIdDisable(id);
      if (response.status === 200) {
        // Substitui o plano pela versão desativada retornada.
        const updated = response.data.data;
        setPlans((prev) => prev.map((p) => (p.id === id ? updated : p)));
      } else {
        setDisableError((prev) => ({
          ...prev,
          [id]: errorMessage(
            response.data,
            `Falha ao desativar (status ${response.status}).`,
          ),
        }));
      }
    } catch (err) {
      console.error("Erro ao desativar plano DCAI:", err);
      setDisableError((prev) => ({
        ...prev,
        [id]: err instanceof Error ? err.message : "Erro desconhecido",
      }));
    } finally {
      setDisablingId(null);
    }
  };

  const loadCycles = useCallback(
    async (id: string, opts?: { silent?: boolean }) => {
      if (!opts?.silent) {
        setCycles((prev) => ({
          ...prev,
          [id]: { loading: true, error: null, items: [], loaded: false },
        }));
      }
      try {
        const response = await getDcaiPlansIdCycles(id);
        if (response.status === 200) {
          const items = response.data.data.filter(
            (c): c is DcaiCycle => c !== null,
          );
          setCycles((prev) => ({
            ...prev,
            [id]: { loading: false, error: null, items, loaded: true },
          }));
        } else {
          setCycles((prev) => ({
            ...prev,
            [id]: {
              loading: false,
              error: errorMessage(
                response.data,
                `Falha ao buscar ciclos (status ${response.status}).`,
              ),
              items: [],
              loaded: true,
            },
          }));
        }
      } catch (err) {
        console.error("Erro ao buscar ciclos DCAI:", err);
        setCycles((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            error: err instanceof Error ? err.message : "Erro desconhecido",
            items: [],
            loaded: true,
          },
        }));
      }
    },
    [],
  );

  // Atualização automática: o cron processa ciclos em segundo plano (sobretudo
  // em planos MINUTELY), então recarregamos planos e ciclos abertos a cada 3s
  // sem flicker — assim os contadores (ex.: ciclos concluídos) sobem sozinhos.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchPlans({ silent: true });
      const id = expandedIdRef.current;
      if (id) loadCycles(id, { silent: true });
    }, 3000);
    return () => clearInterval(interval);
  }, [fetchPlans, loadCycles]);

  const handleProcessCycle = async (id: string) => {
    setProcessingId(id);
    setProcessError((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setProcessResult((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const body: ProcessDcaiCycleBodyDTO = {};
      const category = forceCategory[id];
      if (category) {
        body.forceCategory =
          category as ProcessDcaiCycleBodyDTO["forceCategory"];
      }
      const price = forcePrice[id]?.trim();
      if (price) {
        body.forcePrice = price;
      }
      const response = await postDcaiTemporalPlansIdProcessCycle(id, body);
      if (response.status === 200) {
        const cycle = response.data.data;
        setProcessResult((prev) => ({
          ...prev,
          [id]: cycle
            ? `Ciclo #${cycle.sequence}: ${
                CYCLE_STATUS_LABELS[cycle.status] ?? cycle.status
              }`
            : "Ciclo processado.",
        }));
        // Atualiza contadores/nextRunAt e recarrega os ciclos se expandido.
        await fetchPlans({ silent: true });
        if (expandedId === id) {
          await loadCycles(id);
        } else {
          // Invalida o cache para recarregar ao expandir.
          setCycles((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        }
      } else {
        setProcessError((prev) => ({
          ...prev,
          [id]: errorMessage(
            response.data,
            `Falha ao processar ciclo (status ${response.status}).`,
          ),
        }));
      }
    } catch (err) {
      console.error("Erro ao processar ciclo DCAI:", err);
      setProcessError((prev) => ({
        ...prev,
        [id]: err instanceof Error ? err.message : "Erro desconhecido",
      }));
    } finally {
      setProcessingId(null);
    }
  };

  const toggleCycles = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    // Recarrega sempre ao abrir, para refletir ciclos novos sem precisar
    // sair e voltar à tela.
    loadCycles(id);
  };

  return (
    <div className="page-centered">
      <div className="dcai-wrap">
        <header className="dcai-header">
          <button
            type="button"
            className="dcai-back"
            onClick={() => navigate("/profile")}
          >
            ← Voltar
          </button>
          <h1 className="dcai-title">🤖 DCAI</h1>
        </header>

        <p className="dcai-subtitle">
          Planos de aporte recorrente em cripto com contribuição ajustada por
          IA.
        </p>

        {pnlSummary && (
          <section className="dcai-pnlSummary">
            <div className="dcai-pnlSummaryHead">
              <div className="dcai-pnlSummaryIntro">
                <span className="dcai-pnlSummaryLabel">PnL agregado DCAI</span>
                <strong className="dcai-pnlSummaryTitle">
                  {pnlSummary.quoteCoin ?? "USDT"}
                </strong>
              </div>
              <span
                className={`dcai-pnlSummaryValue ${pnlValueClass(pnlSummary.unrealizedPnl)}`}
              >
                {formatPnlCombined(
                  pnlSummary.unrealizedPnl,
                  pnlSummary.pnlPercent,
                  pnlSummary.quoteCoin,
                )}
              </span>
            </div>

            <div className="dcai-pnlSummaryGrid">
              <div>
                <span className="dcai-pnlSummaryMetricLabel">Investido</span>
                <span className="dcai-pnlSummaryMetricValue">
                  {formatCurrencyLabel(
                    pnlSummary.invested,
                    pnlSummary.quoteCoin,
                  )}
                </span>
              </div>
              <div>
                <span className="dcai-pnlSummaryMetricLabel">Mercado</span>
                <span className="dcai-pnlSummaryMetricValue">
                  {pnlSummary.marketValue == null
                    ? "—"
                    : formatCurrencyLabel(
                        pnlSummary.marketValue,
                        pnlSummary.quoteCoin,
                      )}
                </span>
              </div>
              <div>
                <span className="dcai-pnlSummaryMetricLabel">PnL %</span>
                <span
                  className={`dcai-pnlSummaryMetricValue ${pnlValueClass(pnlSummary.pnlPercent)}`}
                >
                  {pnlSummary.pnlPercent == null
                    ? "—"
                    : `${formatSignedDecimalValue(pnlSummary.pnlPercent)}%`}
                </span>
              </div>
              <div>
                <span className="dcai-pnlSummaryMetricLabel">
                  Atualizado em
                </span>
                <span className="dcai-pnlSummaryMetricValue">
                  {formatDateTime(pnlSummary.priceAsOf)}
                </span>
              </div>
            </div>

            {pnlSummary.partial && (
              <div className="dcai-pnlSummaryNote">
                Algumas moedas ficaram sem cotação; os totais consideram apenas
                os ativos com preço disponível.
              </div>
            )}

            {pnlSummary.byCoin.length > 0 && (
              <div className="dcai-pnlCoins">
                {pnlSummary.byCoin.map((coin) => (
                  <div
                    key={`${coin.baseCoin}-${coin.quoteCoin}`}
                    className="dcai-pnlCoin"
                  >
                    <div className="dcai-pnlCoinHead">
                      <strong className="dcai-pnlCoinName">
                        {coin.baseCoin}/{coin.quoteCoin}
                      </strong>
                      <span
                        className={`dcai-pnlCoinValue ${pnlValueClass(coin.unrealizedPnl)}`}
                      >
                        {formatPnlCombined(
                          coin.unrealizedPnl,
                          coin.pnlPercent,
                          coin.quoteCoin,
                        )}
                      </span>
                    </div>
                    <div className="dcai-pnlCoinMeta">
                      <span>
                        Investido:{" "}
                        {formatCurrencyLabel(coin.invested, coin.quoteCoin)}
                      </span>
                      <span>Custódia: {coin.frozenBalance}</span>
                      <span>
                        {coin.custodyReconciled
                          ? "Reconciliado"
                          : "Divergência de custódia"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {pnlError && !pnlSummary && (
          <div className="dcai-status dcai-status--error">
            PnL indisponível: {pnlError}
          </div>
        )}

        <button
          type="button"
          className="dcai-newBtn"
          onClick={() => {
            setShowForm((v) => !v);
            setFormError(null);
          }}
        >
          {showForm ? "Cancelar" : "+ Novo plano"}
        </button>

        {showForm && (
          <form className="dcai-form" onSubmit={handleCreate}>
            <label className="dcai-field">
              <span>Nome do plano (opcional)</span>
              <input
                type="text"
                value={form.name ?? ""}
                placeholder="Meu DCAI semanal"
                maxLength={255}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
              />
            </label>

            <div className="dcai-fieldRow">
              <label className="dcai-field">
                <span>Moeda comprada</span>
                <input
                  type="text"
                  value={form.baseCoin}
                  placeholder="BTC"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, baseCoin: e.target.value }))
                  }
                />
              </label>
              <label className="dcai-field">
                <span>Moeda de pagamento</span>
                <input
                  type="text"
                  value={form.quoteCoin}
                  placeholder="USDT"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, quoteCoin: e.target.value }))
                  }
                />
              </label>
            </div>

            <label className="dcai-field">
              <span>Aporte por ciclo ({form.quoteCoin || "quote"})</span>
              <input
                type="text"
                inputMode="decimal"
                value={form.contributionAmount}
                placeholder="500"
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    contributionAmount: e.target.value,
                  }))
                }
              />
            </label>

            <label className="dcai-field">
              <span>Frequência</span>
              <select
                value={form.frequency}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    frequency: e.target.value as CreateDcaiPlanDTO["frequency"],
                  }))
                }
              >
                {Object.values(CreateDcaiPlanDTOFrequency).map((freq) => (
                  <option key={freq} value={freq}>
                    {FREQUENCY_LABELS[freq] ?? freq}
                  </option>
                ))}
              </select>
            </label>

            {formError && <div className="dcai-formError">{formError}</div>}

            <button
              type="submit"
              className="dcai-submitBtn"
              disabled={submitting}
            >
              {submitting ? "Criando..." : "Criar plano"}
            </button>
          </form>
        )}

        {loading && <div className="dcai-status">Carregando...</div>}

        {error && !loading && (
          <div className="dcai-status dcai-status--error">{error}</div>
        )}

        {!loading && !error && plans.length === 0 && (
          <div className="dcai-status">Nenhum plano ainda.</div>
        )}

        {!loading &&
          !error &&
          plans.map((plan) => {
            const cyclesState = cycles[plan.id];
            const isExpanded = expandedId === plan.id;
            const planPnl = pnlSummary?.byPlan.find(
              (item) => item.planId === plan.id,
            );
            return (
              <div key={plan.id} className="dcai-card">
                <div className="dcai-cardHead">
                  <div className="dcai-cardTitle">
                    <span className="dcai-cardName">{plan.name}</span>
                    <span className="dcai-cardSymbol">{plan.symbol}</span>
                    <span className="dcai-cardId" title={plan.id}>
                      ID: {plan.id}
                    </span>
                  </div>
                  <span
                    className={`dcai-badge dcai-badge--${plan.status.toLowerCase()}`}
                  >
                    {PLAN_STATUS_LABELS[plan.status] ?? plan.status}
                  </span>
                </div>

                <div className="dcai-cardMeta">
                  <div>
                    <span className="dcai-metaLabel">Frequência</span>
                    <span className="dcai-metaValue">
                      {FREQUENCY_LABELS[plan.frequency] ?? plan.frequency}
                    </span>
                  </div>
                  <div>
                    <span className="dcai-metaLabel">Aporte</span>
                    <span className="dcai-metaValue">
                      {plan.contributionAmount} {plan.quoteCoin}
                    </span>
                  </div>
                  <div>
                    <span className="dcai-metaLabel">Próxima execução</span>
                    <span className="dcai-metaValue">
                      {formatDateTime(plan.nextRunAt)}
                    </span>
                  </div>
                  <div>
                    <span className="dcai-metaLabel">Acumulado</span>
                    <span className="dcai-metaValue">
                      {plan.accumulatedBaseAmount} {plan.baseCoin}
                    </span>
                  </div>
                  <div>
                    <span className="dcai-metaLabel">Ciclos concluídos</span>
                    <span className="dcai-metaValue">
                      {plan.completedCyclesCount}/{plan.totalCyclesCount}
                    </span>
                  </div>
                  <div>
                    <span className="dcai-metaLabel">Ciclos pulados</span>
                    <span className="dcai-metaValue">
                      {plan.skippedCyclesCount}
                    </span>
                  </div>
                </div>

                {planPnl && (
                  <div className="dcai-planPnl">
                    <div className="dcai-planPnlHead">
                      <span className="dcai-planPnlLabel">PnL do plano</span>
                      <span
                        className={`dcai-planPnlValue ${pnlValueClass(planPnl.unrealizedPnl)}`}
                      >
                        {formatPnlCombined(
                          planPnl.unrealizedPnl,
                          planPnl.pnlPercent,
                          planPnl.quoteCoin,
                        )}
                      </span>
                    </div>
                    <div className="dcai-planPnlGrid">
                      <div>
                        <span className="dcai-planPnlMetricLabel">
                          Investido
                        </span>
                        <span className="dcai-planPnlMetricValue">
                          {formatCurrencyLabel(
                            planPnl.invested,
                            planPnl.quoteCoin,
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="dcai-planPnlMetricLabel">Mercado</span>
                        <span className="dcai-planPnlMetricValue">
                          {planPnl.marketValue == null
                            ? "—"
                            : formatCurrencyLabel(
                                planPnl.marketValue,
                                planPnl.quoteCoin,
                              )}
                        </span>
                      </div>
                      <div>
                        <span className="dcai-planPnlMetricLabel">Qty</span>
                        <span className="dcai-planPnlMetricValue">
                          {planPnl.qty} {planPnl.baseCoin}
                        </span>
                      </div>
                      <div>
                        <span className="dcai-planPnlMetricLabel">PnL %</span>
                        <span
                          className={`dcai-planPnlMetricValue ${pnlValueClass(planPnl.pnlPercent)}`}
                        >
                          {planPnl.pnlPercent == null
                            ? "—"
                            : `${formatSignedDecimalValue(planPnl.pnlPercent)}%`}
                        </span>
                      </div>
                    </div>
                    <div className="dcai-planPnlFooter">
                      {planPnl.closed
                        ? "Plano encerrado e ativo devolvido em espécie."
                        : planPnl.priceUnavailable
                          ? "Cotação indisponível no momento."
                          : `Preço médio ${planPnl.avgBuyPrice} ${planPnl.quoteCoin}`}
                    </div>
                  </div>
                )}

                <div className="dcai-cardActions">
                  <button
                    type="button"
                    className="dcai-cardBtn"
                    onClick={() => toggleCycles(plan.id)}
                  >
                    {isExpanded ? "Ocultar ciclos" : "Ver ciclos"}
                  </button>
                  {plan.status === "ACTIVE" && (
                    <button
                      type="button"
                      className="dcai-cardBtn dcai-cardBtn--danger"
                      onClick={() => handleDisable(plan.id)}
                      disabled={disablingId === plan.id}
                    >
                      {disablingId === plan.id ? "Desativando..." : "Desativar"}
                    </button>
                  )}
                </div>

                {disableError[plan.id] && (
                  <div className="dcai-cardError">{disableError[plan.id]}</div>
                )}

                {plan.status === "ACTIVE" && (
                  <div className="dcai-process">
                    <span className="dcai-processTitle">
                      Processar ciclo manualmente (QA)
                    </span>
                    <div className="dcai-processRow">
                      <select
                        className="dcai-processSelect"
                        value={forceCategory[plan.id] ?? ""}
                        onChange={(e) =>
                          setForceCategory((prev) => ({
                            ...prev,
                            [plan.id]: e.target.value,
                          }))
                        }
                      >
                        <option value="">Sinal automático</option>
                        {Object.values(
                          ProcessDcaiCycleBodyDTOForceCategory,
                        ).map((cat) => (
                          <option key={cat} value={cat}>
                            {SIGNAL_LABELS[cat] ?? cat}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="dcai-processSelect"
                        value={forcePrice[plan.id] ?? ""}
                        placeholder={`Preço (${plan.quoteCoin}) — opcional`}
                        onChange={(e) =>
                          setForcePrice((prev) => ({
                            ...prev,
                            [plan.id]: e.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="dcai-cardBtn"
                        onClick={() => handleProcessCycle(plan.id)}
                        disabled={processingId === plan.id}
                      >
                        {processingId === plan.id
                          ? "Processando..."
                          : "▶ Processar ciclo"}
                      </button>
                    </div>
                    {processResult[plan.id] && (
                      <div className="dcai-processResult">
                        {processResult[plan.id]}
                      </div>
                    )}
                    {processError[plan.id] && (
                      <div className="dcai-cardError">
                        {processError[plan.id]}
                      </div>
                    )}
                  </div>
                )}

                {isExpanded && (
                  <div className="dcai-cycles">
                    <div className="dcai-cyclesHead">
                      <span className="dcai-cyclesTitle">Ciclos</span>
                      <button
                        type="button"
                        className="dcai-cardBtn"
                        onClick={() => loadCycles(plan.id)}
                        disabled={cyclesState?.loading}
                      >
                        {cyclesState?.loading
                          ? "Atualizando..."
                          : "🔄 Atualizar"}
                      </button>
                    </div>
                    {cyclesState?.loading && (
                      <div className="dcai-status">Carregando ciclos...</div>
                    )}
                    {cyclesState?.error && (
                      <div className="dcai-status dcai-status--error">
                        {cyclesState.error}
                      </div>
                    )}
                    {cyclesState?.loaded &&
                      !cyclesState.error &&
                      cyclesState.items.length === 0 && (
                        <div className="dcai-status">Nenhum ciclo ainda.</div>
                      )}
                    {cyclesState?.items.map((cycle) => (
                      <div key={cycle.id} className="dcai-cycle">
                        <div className="dcai-cycleHead">
                          <span className="dcai-cycleSeq">
                            #{cycle.sequence}
                          </span>
                          <span
                            className={`dcai-cycleStatus dcai-cycleStatus--${cycle.status.toLowerCase()}`}
                          >
                            {CYCLE_STATUS_LABELS[cycle.status] ?? cycle.status}
                          </span>
                        </div>
                        <div className="dcai-cycleMeta">
                          <span>
                            {formatDateTime(
                              cycle.processedAt ?? cycle.scheduledFor,
                            )}
                          </span>
                          {cycle.signalCategory && (
                            <span>
                              Sinal:{" "}
                              {SIGNAL_LABELS[cycle.signalCategory] ??
                                cycle.signalCategory}
                              {cycle.signalMultiplier
                                ? ` (×${cycle.signalMultiplier})`
                                : ""}
                            </span>
                          )}
                          {cycle.status === "COMPLETED" && (
                            <span>
                              Comprado: {cycle.assetBought} {plan.baseCoin} por{" "}
                              {cycle.finalBuyAmount} {plan.quoteCoin}
                            </span>
                          )}
                          {cycle.skipReason && (
                            <span>Motivo: {cycle.skipReason}</span>
                          )}
                          {cycle.errorReason && (
                            <span>Erro: {cycle.errorReason}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
